import { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { BusLinesPlaceItalie } from '../components/BusLinesPlaceItalie';
import { Countdown } from '../components/Countdown';
import { QuestPoinconHotspot } from '../components/QuestPoinconHotspot';
import { SectionAsterism } from '../components/SectionAsterism';
import { useFlagSearch } from '../FeatureFlagsContext';
import { WEDDING_DATE, usePhotoboothEnabled } from '../weddingCountdown';

export function HomePage() {
  const location = useLocation();
  const navigate = useNavigate();
  const showPhotobooth = usePhotoboothEnabled();
  const flagSearch = useFlagSearch();

  useEffect(() => {
    const section = (location.state as { section?: string } | null)?.section;
    if (!section) return;
    const allowed = ['accueil', 'programme', 'lieu', 'hebergement', 'rsvp'].includes(section);
    if (allowed) {
      requestAnimationFrame(() => {
        document.getElementById(section)?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
    navigate({ pathname: location.pathname, search: location.search }, { replace: true, state: {} });
  }, [location, navigate]);

  return (
    <main>
      <section id="accueil" className="section section--hero" aria-labelledby="hero-title">
        <div
          className="section--hero__bg"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1519741497674-611481863552?auto=format&fit=crop&w=1920&q=80')",
          }}
          role="img"
          aria-label="Alliances sur un livre ouvert, photo décorative"
        />
        <div className="section--hero__overlay" />
        <div className="section--hero__content">
          <h1 id="hero-title" className="section--hero__title">
            Virginie
            <br />
            &amp;
            <br />
            Jean-Christophe
          </h1>
          <p className="section--hero__date">Samedi 18 juillet 2026 · Paris</p>
          <Countdown targetDate={WEDDING_DATE} />
          {showPhotobooth && (
            <p className="section--hero__photo-cta">
              <Link to={{ pathname: '/photos', search: flagSearch }} className="section--hero__photo-link">
                <span aria-hidden="true">📸</span>
                Photobooth
              </Link>
            </p>
          )}
          <p className="section--hero__hint">Faites défiler pour en savoir plus</p>
        </div>
      </section>

      <section id="deroule" className="section section--alt" aria-labelledby="deroule-title">
        <div className="section__inner section__inner--deroule">
          <div className="section__text">
            <h2 id="deroule-title">Déroulé</h2>
            <ul className="timeline">
            <li>
                <span className="timeline__time">11h30</span>
                <span className="timeline__event">Rendez-vous devant la mairie du 13e arrondissement de Paris</span>
              </li>
              <li>
                <span className="timeline__time">11h45</span>
                <span className="timeline__event">Cérémonie civile</span>
              </li>
              <li>
                <span className="timeline__time">13h00</span>
                <span className="timeline__event">Repas au restaurant Poinçon, Paris 14e</span>
              </li>
              <li>
                <span className="timeline__time">17h00</span>
                <span className="timeline__event">Fin du repas</span>
                <span className="timeline__event"><i>Pour celles et ceux qui le souhaitent, nous passerons ensuite au parc Montsouris avec les enfants.</i></span>
              </li>
            </ul>
            <SectionAsterism />
          </div>
        </div>
      </section>

      <section id="ceremonie" className="section" aria-labelledby="ceremonie-title">
        <div className="section__inner section__inner--reverse">
          <figure className="section__figure section__figure--landscape">
            <img
              src={`${process.env.PUBLIC_URL || ''}/images/Mairie.jpg`}
              alt="Façade de la mairie du 13e arrondissement de Paris, sous un ciel bleu"
              width={1448}
              height={1086}
              loading="lazy"
            />
          </figure>
          <div className="section__text">
            <h2 id="ceremonie-title">Cérémonie</h2>
            <p className="section__lead">
              <strong>Mairie du 13e</strong>
              <br />Rendez-vous sur le parvis
              <br /><i>Les valises ne sont pas autorisées dans la mairie.</i>
            </p>
            <p>
              <strong>Adresse :</strong> 1, Place d'Italie - 75013 Paris
            </p>
            <p>
              <strong>Accès :</strong>
              <br />Bus <BusLinesPlaceItalie />
              <br />Métro lignes{' '}
              <span className="metro-badge metro-badge--5" aria-label="Métro ligne 5">
                5
              </span>{' '}
              <span className="metro-badge metro-badge--6" aria-label="Métro ligne 6">
                6
              </span>{' '}
              et{' '}
              <span className="metro-badge metro-badge--7" aria-label="Métro ligne 7">
                7
              </span>
              , <strong>arrêt</strong> Place d'Italie, sortie 4 Mairie du 13e
            </p>
          </div>
        </div>
      </section>

      <section id="repas" className="section section--alt" aria-labelledby="repas-title">
        <div className="section__inner">
          <figure className="section__figure section__figure--poincon">
            <div className="section__figure__media">
              <img
                src={`${process.env.PUBLIC_URL || ''}/images/Poinçon.jpg`}
                alt="Restaurant Poinçon, Paris 14e"
                width={1582}
                height={994}
                loading="lazy"
              />
              <QuestPoinconHotspot />
            </div>
          </figure>
          <div className="section__text">
            <h2 id="repas-title">Repas</h2>
            <p className="section__lead"><strong>Restaurant Poinçon</strong>
            <br />Le déjeuner prendra la forme d'un buffet avec des propositions carnées et végétariennes. Le placement est libre.
            <br />Deux espaces seront mis à disposition : la mezzanine intérieure ainsi que la terrasse extérieure, située au rez-de-chaussée.
            <br />Trois personnes seront présentes dans l'appartement du chef de gare, à l'étage supérieur, pour faire manger, jouer et s'occuper des enfants.
            <br />Le restaurant est climatisé et dispose d'un ascenceur pour accéder à la mezzanine.
            </p>
            <p>
            <strong>Adresse :</strong> 124, Avenue du Général Leclerc - 75014 Paris
            </p>
            <p>
              <strong>Accès :</strong>
              <br />Tram{' '}
              <span className="tram-badge tram-badge--t3a" aria-label="Tramway T3a">
                T3a
              </span>{' '}
              <br />Métro ligne{' '}
              <span className="metro-badge metro-badge--4" aria-label="Métro ligne 4">
                4
              </span>
            , <strong>arrêt</strong> Porte d'Orléans, sortie 7 Rue Poirier de Narçay
            </p>
            <p>
              Depuis la mairie, nous prendrons la ligne{' '}
              <span className="metro-badge metro-badge--6" aria-label="Métro ligne 6">
                6
              </span>{' '}
              direction Charles de Gaulle-Etoile, jusqu&apos;à la station <strong>Raspail</strong>, puis la ligne{' '}
              <span className="metro-badge metro-badge--4" aria-label="Métro ligne 4">
                4
              </span>{' '}
              direction Bagneux - Lucie Aubrac, jusqu&apos;à la station <strong>Porte d&apos;Orléans</strong>. Le restaurant se situe en face de la sortie 7 Rue Poirier de Narçay. Le trajet dure environ 20 minutes.
            </p>
          </div>
        </div>
      </section>

      <section id="cagnotte" className="section section--rsvp" aria-labelledby="cagnotte-title">
        <div className="section__inner section__inner--narrow">
          <h2 id="cagnotte-title">Cagnotte</h2>
          <p className="section__lead">
            Pour celles et ceux qui souhaiteraient participer à notre voyage au Japon{' '}
            <img
              src={`${process.env.PUBLIC_URL || ''}/japan-flag.svg`}
              alt=""
              className="inline-flag-jp"
              width={28}
              height={19}
              decoding="async"
            />
            {' '}, une cagnotte est disponible ci-dessous jusqu'au dimanche 26 juillet 2026.
          </p>
          <a
            href="https://www.onparticipe.fr/c/We3FKuIb"
            className="btn-cagnotte"
            target="_blank"
            rel="noopener noreferrer"
          >
            Participer
          </a>
        </div>
      </section>
    </main>
  );
}
